import { deg, fixed, inches, trim } from './format';
import { require, requireFinite, requirePositive, step, type Solved } from './types';

export const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
export const toDegrees = (radians: number): number => (radians * 180) / Math.PI;

// ── Right-triangle trig ──────────────────────────────────────────────────────

export type Side = 'opp' | 'adj' | 'hyp';

const SIDE_NAME: Record<Side, string> = {
  opp: 'opposite',
  adj: 'adjacent',
  hyp: 'hypotenuse',
};

/**
 * The function you need, given which side you have and which you want.
 * This lookup *is* the lesson — SOH CAH TOA restated as a decision.
 */
export function chooseFunction(have: Side, want: Side): Solved<'sin' | 'cos' | 'tan'> {
  require(have !== want, 'The side you have and the side you want must differ');
  const pair = new Set([have, want]);
  const fn = !pair.has('hyp') ? 'tan' : pair.has('opp') ? 'sin' : 'cos';
  const definition = { sin: 'sin = opp ÷ hyp', cos: 'cos = adj ÷ hyp', tan: 'tan = opp ÷ adj' }[fn];

  return {
    value: fn,
    steps: [
      step('formula', `You have the ${SIDE_NAME[have]} and want the ${SIDE_NAME[want]}.`),
      step('compute', `The function that ties those two together is ${fn}.`),
      step('result', definition),
      step(
        'note',
        'Name the sides from the angle you are working off, not from how the drawing is turned.',
      ),
    ],
  };
}

/** Solve for a missing side given one angle and one known side. */
export function solveSide(
  angleDegrees: number,
  have: Side,
  haveLength: number,
  want: Side,
): Solved<number> {
  requireFinite(angleDegrees, 'angle');
  require(angleDegrees > 0 && angleDegrees < 90, 'Angle must be between 0° and 90°');
  requirePositive(haveLength, 'known side');
  require(have !== want, 'The side you have and the side you want must differ');

  const rad = toRadians(angleDegrees);
  const chosen = chooseFunction(have, want);
  const fn = chosen.value;
  const ratioValue = { sin: Math.sin(rad), cos: Math.cos(rad), tan: Math.tan(rad) }[fn];

  // Each ratio is (numerator side) ÷ (denominator side). If the side you have
  // is the denominator you multiply up; otherwise you divide back down.
  const denominator: Record<typeof fn, Side> = { sin: 'hyp', cos: 'hyp', tan: 'adj' };
  const multiplying = have === denominator[fn];
  const value = multiplying ? haveLength * ratioValue : haveLength / ratioValue;

  return {
    value,
    steps: [
      step('formula', chosen.steps[2]!.text),
      step('compute', `${fn} ${deg(angleDegrees)} = ${fixed(ratioValue, 4)}`),
      step(
        'substitute',
        `${SIDE_NAME[want]} = ${trim(haveLength)} ${multiplying ? '×' : '÷'} ${fixed(ratioValue, 4)}`,
      ),
      step('result', `= ${inches(value)}`),
      step(
        'note',
        'The hypotenuse is always the longest side. If your answer came out longer than it, you multiplied where you should have divided.',
      ),
    ],
  };
}

/** Angle from two known sides — rise over run being the everyday case. */
export function solveAngle(
  first: Side,
  firstLength: number,
  second: Side,
  secondLength: number,
): Solved<number> {
  requirePositive(firstLength, 'first side');
  requirePositive(secondLength, 'second side');
  require(first !== second, 'The two sides must differ');

  const lengths: Partial<Record<Side, number>> = {
    [first]: firstLength,
    [second]: secondLength,
  };
  const fn = chooseFunction(first, second).value;

  let ratio: number;
  let expression: string;
  if (fn === 'tan') {
    ratio = lengths.opp! / lengths.adj!;
    expression = `tan(angle) = ${trim(lengths.opp!)} ÷ ${trim(lengths.adj!)}`;
  } else if (fn === 'sin') {
    ratio = lengths.opp! / lengths.hyp!;
    expression = `sin(angle) = ${trim(lengths.opp!)} ÷ ${trim(lengths.hyp!)}`;
  } else {
    ratio = lengths.adj! / lengths.hyp!;
    expression = `cos(angle) = ${trim(lengths.adj!)} ÷ ${trim(lengths.hyp!)}`;
  }

  require(
    fn === 'tan' || (ratio > 0 && ratio <= 1),
    'That triangle cannot exist — a leg is longer than the hypotenuse',
  );

  const value = toDegrees(fn === 'tan' ? Math.atan(ratio) : fn === 'sin' ? Math.asin(ratio) : Math.acos(ratio));

  return {
    value,
    steps: [
      step('formula', expression),
      step('compute', `= ${fixed(ratio, 4)}`),
      step('substitute', `Now undo it: ${fn}⁻¹(${fixed(ratio, 4)})`),
      step('result', `= ${deg(value)}`),
      step(
        'note',
        'The inverse key is the shifted one — sin⁻¹, cos⁻¹, tan⁻¹. It turns a ratio back into an angle.',
      ),
    ],
  };
}

/** Third side of a right triangle from the other two. */
export function pythagoras(a: number, b: number): Solved<number> {
  requirePositive(a, 'first leg');
  requirePositive(b, 'second leg');
  const value = Math.hypot(a, b);
  return {
    value,
    steps: [
      step('formula', 'hyp² = a² + b²'),
      step('substitute', `${trim(a)}² + ${trim(b)}² = ${fixed(a * a + b * b, 4)}`),
      step('result', `hyp = √${fixed(a * a + b * b, 4)} = ${inches(value)}`),
    ],
  };
}

// ── Bolt circles ─────────────────────────────────────────────────────────────

export interface BoltHole {
  readonly index: number;
  readonly angleDegrees: number;
  readonly x: number;
  readonly y: number;
}

export function holeSpacing(holes: number): Solved<number> {
  require(Number.isInteger(holes) && holes >= 1, 'Hole count must be a whole number of 1 or more');
  const value = 360 / holes;
  return {
    value,
    steps: [
      step('formula', 'angle between holes = 360 ÷ number of holes'),
      step('substitute', `360 ÷ ${holes}`),
      step('result', `= ${deg(value)} between holes`),
    ],
  };
}

/** Straight-line distance between two neighbouring holes. */
export function boltChord(diameter: number, holes: number): Solved<number> {
  requirePositive(diameter, 'bolt circle diameter');
  require(Number.isInteger(holes) && holes >= 2, 'Need at least 2 holes for a chord');

  const halfAngle = 180 / holes;
  const sine = Math.sin(toRadians(halfAngle));
  const value = diameter * sine;

  return {
    value,
    steps: [
      step('formula', 'chord = D × sin(180 ÷ n)'),
      step('substitute', `180 ÷ ${holes} = ${trim(halfAngle, 3)}`),
      step('compute', `sin ${deg(halfAngle, 3)} = ${fixed(sine, 4)}`),
      step('compute', `${trim(diameter)} × ${fixed(sine, 4)}`),
      step('result', `= ${inches(value)}`),
      step(
        'note',
        'Bolt circles are called out by diameter, never radius. The chord is the straight hop between holes, not the arc.',
      ),
    ],
  };
}

/**
 * X/Y coordinates for every hole.
 *
 * Angles run counterclockwise from 3 o'clock, which is the machine convention
 * and matches how the drill table on a print is laid out. `startAngle` moves
 * the first hole; the common alternative is 90° to put a hole at 12 o'clock.
 */
export function boltHoles(diameter: number, holes: number, startAngle = 0): Solved<BoltHole[]> {
  requirePositive(diameter, 'bolt circle diameter');
  require(Number.isInteger(holes) && holes >= 1, 'Hole count must be a whole number of 1 or more');
  requireFinite(startAngle, 'start angle');

  const radius = diameter / 2;
  const spacing = 360 / holes;
  const value: BoltHole[] = Array.from({ length: holes }, (_, index) => {
    const angleDegrees = (startAngle + index * spacing) % 360;
    const rad = toRadians(angleDegrees);
    return {
      index,
      angleDegrees,
      x: radius * Math.cos(rad),
      y: radius * Math.sin(rad),
    };
  });

  return {
    value,
    steps: [
      step('formula', 'X = R × cos(angle), Y = R × sin(angle)'),
      step('compute', `R = ${trim(diameter)} ÷ 2 = ${inches(radius)}`),
      step('compute', `${holes} holes, so ${deg(spacing)} apart`),
      step('result', `Hole 1 sits at ${deg(startAngle)}, X ${inches(value[0]!.x)}, Y ${inches(value[0]!.y)}`),
      step('note', 'Angles run counterclockwise from 3 o’clock, measured from the circle centre.'),
    ],
  };
}

// ── Tapers ───────────────────────────────────────────────────────────────────

export interface Taper {
  /** Taper per inch of length, on the diameter. */
  readonly tpi: number;
  /** Taper per foot. */
  readonly tpf: number;
  /** Full included angle of the taper, degrees. */
  readonly includedAngle: number;
  /** What the compound gets set to — half the included angle. */
  readonly compoundAngle: number;
}

export function taperFromDiameters(
  largeDiameter: number,
  smallDiameter: number,
  length: number,
): Solved<Taper> {
  requirePositive(largeDiameter, 'large diameter');
  requirePositive(smallDiameter, 'small diameter');
  requirePositive(length, 'length');
  require(largeDiameter > smallDiameter, 'The large diameter must be bigger than the small one');

  const difference = largeDiameter - smallDiameter;
  const tpi = difference / length;
  const tpf = tpi * 12;
  // The angle is per side, so the diameter difference is halved before the atan.
  const compoundAngle = toDegrees(Math.atan(difference / 2 / length));
  const includedAngle = compoundAngle * 2;

  return {
    value: { tpi, tpf, includedAngle, compoundAngle },
    steps: [
      step('formula', 'TPI = (big Ø − small Ø) ÷ length'),
      step('substitute', `(${trim(largeDiameter)} − ${trim(smallDiameter)}) ÷ ${trim(length)}`),
      step('compute', `${trim(largeDiameter)} − ${trim(smallDiameter)} = ${fixed(difference, 4)}`),
      step('result', `= ${inches(tpi)} per inch, which is ${inches(tpf)} per foot`),
      step(
        'note',
        `The compound gets half the included angle: ${deg(includedAngle)} included, so set ${deg(compoundAngle)}. It only cuts one side.`,
      ),
    ],
  };
}

export function taperPerInchFromPerFoot(tpf: number): Solved<number> {
  requirePositive(tpf, 'taper per foot');
  const value = tpf / 12;
  return {
    value,
    steps: [
      step('formula', 'TPI = TPF ÷ 12'),
      step('substitute', `${trim(tpf)} ÷ 12`),
      step('result', `= ${inches(value)} per inch`),
      step('note', 'A foot is 12 inches, so the same taper spread over 12 times the length.'),
    ],
  };
}

// ── Sine bar ─────────────────────────────────────────────────────────────────

/** Gauge block stack to tip a sine bar to an angle. */
export function sineBarStack(barLength: number, angleDegrees: number): Solved<number> {
  requirePositive(barLength, 'sine bar length');
  require(angleDegrees > 0 && angleDegrees < 90, 'Angle must be between 0° and 90°');

  const sine = Math.sin(toRadians(angleDegrees));
  const value = barLength * sine;

  return {
    value,
    steps: [
      step('formula', 'stack = sine bar length × sin(angle)'),
      step('compute', `sin ${deg(angleDegrees)} = ${fixed(sine, 5)}`),
      step('substitute', `${trim(barLength)} × ${fixed(sine, 5)}`),
      step('result', `= ${inches(value)} of gauge blocks`),
      step(
        'note',
        'The bar length is centre-to-centre of the rolls, not the overall length of the bar.',
      ),
    ],
  };
}

/** The angle a known stack produces — checking a setup that is already built. */
export function sineBarAngle(barLength: number, stackHeight: number): Solved<number> {
  requirePositive(barLength, 'sine bar length');
  requirePositive(stackHeight, 'stack height');
  require(stackHeight <= barLength, 'The stack cannot be taller than the sine bar is long');

  const ratio = stackHeight / barLength;
  const value = toDegrees(Math.asin(ratio));

  return {
    value,
    steps: [
      step('formula', 'sin(angle) = stack ÷ sine bar length'),
      step('substitute', `${trim(stackHeight)} ÷ ${trim(barLength)} = ${fixed(ratio, 5)}`),
      step('result', `angle = sin⁻¹(${fixed(ratio, 5)}) = ${deg(value)}`),
    ],
  };
}
